import { IFeedbackResponse, IOngoingFeedBack, ISurvey } from './../Types/Interface/ISurvey';
import { useAppSelector } from 'Hooks';
import { CampaignType, FeedbackStatus } from 'Types/Enums';
import { IBrand } from 'Types/Interface';
import { AppRoutes } from 'Routes/AppRoutes';

/**
 * Determines if the survey is new (not yet started) based on survey type and status
 * @param responses - Array of survey responses
 * @param status - Current feedback status
 * @param chatbaseAgentId - Optional chatbase agent ID (if present, survey uses chatbase)
 * @returns True if the survey is new and hasn't been started yet
 */
export function getIsNewSurvey(
	responses: IFeedbackResponse[] | undefined,
	status: FeedbackStatus,
	chatbaseAgentId?: string
): boolean {
	const isChatbaseSurvey = !!chatbaseAgentId;
	const hasNoResponses = !responses || responses.length === 0;
	
	// For chatbase surveys, check if status is Ongoing
	if (isChatbaseSurvey) {
		return status === FeedbackStatus.Ongoing;
	}
	
	// For regular surveys, check if there are no responses
	return hasNoResponses;
}

/**
 * Custom hook to determine if the survey is new (not yet started)
 * Uses Redux state internally to simplify component usage
 * @returns True if the survey is new and hasn't been started yet
 */
export function useIsNewSurvey(): boolean {
	const {
		activeSurvey: { responses, status, survey },
	} = useAppSelector((state) => state.survey);

	return getIsNewSurvey(
		responses,
		status,
		survey?.chatbaseAgentId
	);
}

export function isLastSurveySection() {
	const {
		activeSurvey: { survey },
		section,
	} = useAppSelector((state) => state.survey);

	// For chatbase surveys, brand permissions is always the last section
	if (survey.chatbaseAgentId) {
		return section === 'brandSharingSettings';
	}

	return (
		section === 'brandSharingSettings' || (section === 'questions' && survey?.campaignType === CampaignType.feedbackOnly)
	);
}

export function getMaxRewardPoints(survey: ISurvey | undefined): number {
	if (survey === undefined) return 0;
	return (1 + getInitialScanPointPercentage(survey) / 100) * getPointsPerFeedback(survey) + survey.higherSharingPoints;
}

export function getPointsPerFeedback(survey: ISurvey | undefined): number {
	return survey?.rewards?.[0]?.pointsPerFeedback ?? 0;
}

export function getInitialScanPointPercentage(survey: ISurvey | undefined): number {
	return survey?.rewards?.[0]?.initialScanPointsPercentage ?? 0;
}

export function getSurveyImage(survey: IOngoingFeedBack | ISurvey): string | undefined {
	if (survey.product?.images?.length && survey.product.images[0].imageUrl) {
		return survey.product.images[0].imageUrl;
	}
	if (survey.brand?.logoUrl) {
		return survey.brand.logoUrl;
	}
	return undefined;
}

export function getSurveyBrandName(survey: IOngoingFeedBack | ISurvey): string | undefined {
	const brand = getSurveyBrand(survey);
	return brand?.name;
}

export function getSurveyBrand(survey: IOngoingFeedBack | ISurvey): IBrand {
	if (survey?.product?.brand) {
		return survey.product.brand;
	}
	if (survey?.brand) {
		return survey.brand;
	}
	return {} as IBrand;
}

/**
 * Determines the appropriate survey URL based on whether the survey has a chatbaseAgentId
 * @param survey - The survey object containing survey details
 * @param qrCode - The QR code to use in the URL
 * @returns The appropriate survey URL
 */
export function getSurveyUrl(survey: ISurvey, qrCode: string): string {
	// Chatbase surveys now use the same survey route as regular surveys
	return AppRoutes.survey.replace(':webcode', qrCode);
}
